import { startCase } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { SnCamera } from '@lib/api/entities';
import { AiAPI, SnCamerasAPI } from '@lib/api/services';
import { t } from '@lib/translation';
import { SnDrawerService } from '@lib/ui/drawer';

import { CreateSentryDialogComponent } from '../create-sentry-dialog/create-sentry-dialog.component';
import { SentrySearchResultsDrawerComponent } from '../sentry-search-results-drawer/sentry-search-results-drawer.component';
@Component({
  selector: 'fs-snap-ai-selector',
  templateUrl: './snap-ai-selector.component.html',
  styleUrls: ['./snap-ai-selector.component.scss'],
})
export class SnapAiSelectorComponent implements OnInit, OnDestroy {
  @Input()
  set snapAiImage(image: any) {
    this.AiImage = image.file;
    this.camera = image.camera;
    if (this.camera) {
      this.getNearCameras();
    }
    this.isLoading = true;
    this.failedSnapAiImage = false;
    const formData = new FormData();
    formData.append('file', this.AiImage, this.AiImage.name);
    this.dataUrl = URL.createObjectURL(this.AiImage);
    this._getSnapAi(formData)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (value) => {
          this.response = value;
          this.isLoading = false;
          this._cd.detectChanges();
        },
        error: () => {
          this.isLoading = false;
          this.response = false;
          this.failedSnapAiImage = true;
        },
      });
  }

  get createSentry$(): Observable<any[]> {
    return this._createSentry$.asObservable();
  }

  public AiImage: any;
  public camera: SnCamera;
  public cameras: SnCamera[];
  public form = new UntypedFormGroup({});
  public response: any;
  public failedSnapAiImage: boolean;
  public dataUrl = '';
  public item: any;
  public isLoading: boolean;

  public aiCameras: string[];

  public formFields = [];

  private readonly _destroy$ = new Subject();

  private readonly _createSentry$ = new Subject<string[]>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly _aiAPI: AiAPI,
    public readonly _camerasAPI: SnCamerasAPI,
    private readonly _sanitizer: DomSanitizer,
    private readonly _matDialog: MatDialog,
    private readonly _cd: ChangeDetectorRef,
    private readonly _drawer: SnDrawerService,
    private readonly _toastrService: ToastrService,
    private readonly _dialogRef: MatDialogRef<SnapAiSelectorComponent>
  ) {}

  public ngOnInit(): void {
    this._genForm();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public transform(label: string): string {
    return startCase(label);
  }

  public getNearCameras(): void {
    const coords = this.camera.geometry.coordinates;
    this._camerasAPI
      .getRadarCamera(coords[0], coords[1], 2, 1000, this.camera.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((results: SnCamera[]) => {
        this.data = results;
        this._cd.detectChanges();
      });
  }

  public sanitizeImg(url: string): SafeUrl {
    return this._sanitizer.bypassSecurityTrustUrl(url);
  }

  public previewItem(item: any): void {
    this.item = item;
  }

  public createArray(): any {
    const cameraid = this._extractCameraIds();
    this.openSentrySearchDrawer(cameraid);
  }

  public searchResults(): any {
    const cameraid = this._extractCameraIds();
    const formData = this.item;
    const params = this._formToParams(formData, cameraid);

    this._drawer.open(SentrySearchResultsDrawerComponent, {
      data: params,
      mode: 'side',
      width: 380,
      closeOnNavigation: true,
      panelClass: ['hide-on-fullscreen'],
    });
    this._dialogRef.close();
  }

  public openSentrySearchDrawer(cameraList: string[]): void {
    const data: any = {
      aiCameras: cameraList,
      label: this.item.label,
      confidence: this.item.confidence - 20,
    };

    if (this.item.label === 'person') {
      if (this.item?.metadata?.attributes) {
        this.item.metadata.attributes.forEach((item) => {
          data[item.label] = item.confidence > 50 ? '>=50' : '< 50';
        });
      }

      if (this.item?.metadata?.colorBottom?.label) {
        data.colorBottom = this.item.metadata.colorBottom.label;
      }

      if (this.item?.metadata?.colorTop?.label) {
        data.colorTop = this.item.metadata.colorTop.label;
      }
    }

    if (this.item.label === 'car') {
      if (this.item?.metadata?.vehicleType?.[0]?.label) {
        data.vehicleType = this.item.metadata.vehicleType[0].label;
      }
      if (this.item?.metadata?.carColor?.[0]?.label) {
        data.carColors = this.item.metadata.carColor[0].label;
      }
      if (this.item?.metadata?.carColor?.[0]?.confidence) {
        data.colorConfidence = this.item.metadata.carColor[0].confidence;
      }
    }

    this._matDialog.open(CreateSentryDialogComponent, {
      data,
    });
    this._dialogRef.close();
  }

  public resetSearch(): void {
    this.formFields = [];
    this.response = null;
    this.item = null;
    this.isLoading = false;
    this._genForm();
  }

  private _extractCameraIds(): string[] {
    const cameraid = [];

    if (Array.isArray(this.data)) {
      this.data.forEach((element) => {
        const cameraId = element.camera?.id || element.id;
        if (cameraId) {
          cameraid.push(cameraId);
        }
      });
    } else if (this.data?.camera?.id) {
      cameraid.push(this.data.camera.id);
    }

    return cameraid;
  }

  private _formToParams(formData: any, camera: any): any {
    const cameras = camera;
    const date = new Date();
    const offset = (date.getTimezoneOffset() * -1) / 60;
    const startTime = `0, ${offset}`;
    const endTime = `24, ${offset}`;
    const startDate = new Date(new Date().setDate(new Date().getDate() - 1));
    const params: any = {
      camera_id__in: cameras.join(','),
      label__in: formData.label,
      timestamp__gte: Math.floor(startDate.getTime() / 1000),
      timestamp__lte: null,
      timestamp__hour__gte__offset: startTime,
      timestamp__hour__lt__offset: endTime,
      confidence__gte: formData.confidence,
    };

    switch (formData.label) {
      case 'car':
        if (formData?.metadata?.vehicleType?.[0]?.label) {
          params.metadata__vehicle_type__in = formData.metadata.vehicleType[0].label;
        }
        if (formData?.metadata?.carColor?.[0]?.label) {
          params.metadata__car_color__in = formData.metadata.carColor[0].label;
        }
        if (formData?.metadata?.carColor?.[0]?.confidence) {
          params.metadata__car_color__confidence__gte = formData.metadata.carColor[0].confidence;
        }
        break;
      case 'person':
        if (formData?.metadata?.colorTop) {
          params.metadata__color_top__in = formData?.metadata?.colorTop?.label;
        }
        if (formData?.metadata?.colorBottom) {
          params.metadata__color_bottom__in = formData?.metadata?.colorBottom?.label;
        }
        if (formData?.metadata?.attributes) {
          formData.metadata.attributes.forEach((item) => {
            switch (item.label) {
              case 'is_male':
                if (item.confidence >= 50) {
                  params.metadata__attributes__is_male__confidence__gte = 50;
                } else if (item.confidence < 50) {
                  params.metadata__attributes__is_male__confidence__lte = 50;
                }
                break;
              case 'has_hat':
                if (item.confidence >= 50) {
                  params.metadata__attributes__has_hat__confidence__gte = 50;
                } else if (item.confidence < 50) {
                  params.metadata__attributes__has_hat__confidence__lte = 50;
                }
                break;
              case 'has_backpack':
                if (item.confidence >= 50) {
                  params.metadata__attributes__has_backpack__confidence__gte = 50;
                } else if (item.confidence < 50) {
                  params.metadata__attributes__has_backpack__confidence__lte = 50;
                }
                break;
              case 'has_bag':
                if (item.confidence >= 50) {
                  params.metadata__attributes__has_bag__confidence__gte = 50;
                } else if (item.confidence < 50) {
                  params.metadata__attributes__has_bag__confidence__lte = 50;
                }
                break;
            }
          });
        }
        break;
    }
    return params;
  }

  private _getSnapAi(file: any): Observable<any> {
    return this._aiAPI.snapAi(file);
  }

  private _genForm(): void {
    this.formFields = [
      {
        key: 'file',
        type: 'file-upload',
        label: t('map.snapAiSelector.uploadPhoto'),
        templateOptions: {
          accept: '.jpg, .jpeg, .png',
          maxFileSize: 10000000,
          multiple: false,
          change: (fieldValue, fileValue) => {
            if (!fileValue || !fileValue[0]) {
              return;
            }
            if (
              fileValue[0].type === 'image/png' ||
              fileValue[0].type === 'image/jpg' ||
              fileValue[0].type === 'image/jpeg'
            ) {
              this.isLoading = true;
              const formData = new FormData();
              formData.append('file', fileValue[0], fileValue[0].name);
              this.dataUrl = URL.createObjectURL(fileValue[0]);
              this._getSnapAi(formData)
                .pipe(takeUntil(this._destroy$))
                .subscribe((value) => {
                  this.response = value;
                  this.isLoading = false;
                });
            } else {
              this._toastrService.error(
                t('map.snapAiSelector.imageFormatError'),
              );
              this.resetSearch();
              return;
            }
          },
        },
      },
    ];
  }
}
